import '../../../ProductCard/ProductCard.scss';

import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import loadable from '@loadable/component';

import { listerClickThrough } from '../../../../actions/lister-actions';
import { useEventListener } from '../../../../hooks/useEventListener';
import TRANSLATIONS from '../../../../translations/translations@default.json';
import {
	getDeliveryDate,
	getDeliveryDaysDifference,
} from '../../../../utilities/lister-utilities';
import AddToBasketButton from '../../../ProductCard/AddToBasket/AddToBasketButton@mobile';
import ExVat from '../../../ProductCard/ExVat';
import Heading from '../../../ProductCard/Heading';
import MoreInfo from '../../../ProductCard/MoreInfo';
import StockStatus from '../../../ProductCard/StockStatus';
import CustomCatBullets from '../../customCategory/CustomCatBullets';
import EnergyInformation from '../../customCategory/CustomCatEnergyInformation';
import CustomCatMobileDeliveryBlock from '../../customCategory/CustomCatMobileDeliveryBlock';
import CustomCatPricePod from '../../customCategory/CustomCatPricePod@default';
import CustomCatProductImage from '../../customCategory/CustomCatProductImage';
import PromoFlag from '../../customCategory/CustomCatPromoFlag';
import CustomCatReviewRating from '../../customCategory/CustomCatReviewRating';
import { Context } from '../../store/store';
import MobileProductCardSkeleton from './Skeletons/MobileProductCardSkeleton';
import CompareButton from '../../../ProductCard/CompareButton/CompareButton';
import {
	useIsMediumAndAbove,
	useIsBelowMediumOnly,
} from '../../../../hooks/mediaQueries';

const BackInStockForm = loadable(() =>
	import(
		/* webpackPrefetch: true */ '../../../ProductCard/BackInStockForm/BackInStockForm.js'
	)
);

export const CustomCategoryMobileProductCard = ({ product, observer }) => {
	const card = useRef(null);

	const [contextState, contextDispatch] = useContext(Context);
	const { loaded } = contextState;

	const dispatch = useDispatch();

	useEventListener(`lazy-load-${product.ColourId}`, () => {
		contextDispatch({ type: 'UPDATE_LOADED', payload: true });
	});

	useEffect(() => {
		const cardRef = card.current;

		if (observer && cardRef) {
			observer.observe(cardRef);
		}

		return () => {
			if (observer && cardRef) {
				observer.unobserve(cardRef);
			}
		};
	}, [card.current, observer]);

	const deliveryDate = getDeliveryDate(
		product.DynamicDeliveryBulletsViewModel[0]
	);

	const deliveryDaysDifference = getDeliveryDaysDifference(deliveryDate);
	const isMediumAndAbove = useIsMediumAndAbove();
	const isBelowMediumOnly = useIsBelowMediumOnly();

	if (!loaded) {
		return <MobileProductCardSkeleton ColourId={product.ColourId} />;
	}

	return (
		<>
			<a
				href={product.FullProductUrl}
				onClick={(event) => {
					event.preventDefault();
					window.location.assign(product.FullProductUrl);

					dispatch(listerClickThrough(product.Code));
				}}
				className="product-card__link"
				itemProp="url"
				data-testid="product-card-link"
				data-tag-name="lister product card"
				data-tag-type={product.IsFeaturedProduct ? 'featured' : 'standard'}
				data-tag-delivery-date={deliveryDate}
				data-tag-delivery-days={deliveryDaysDifference}>
				<span>
					{TRANSLATIONS.productList.productCard.viewProductAccessibilityText}
				</span>
			</a>
			<PromoFlag {...product} />
			<Heading content={product.FullTitle} />
			<CustomCatReviewRating classes="mt-0" isRecommendedProduct {...product} />
			<CustomCatProductImage {...product} classes="my-4" />
			<StockStatus
				classes="mt-4"
				stockStatus={product.State}
				productCode={product.Code}
				alignCenter
			/>
			<CustomCatBullets content={product.FeatureDescriptions} />
			{product.EnergyRating && (
				<EnergyInformation
					classes="flex items-center mt-4 text-center"
					energyRatingDetails={product.EnergyRatingDetails}
					customCatSmall
				/>
			)}
			<div className="mt-3 xl:mt-0">
				<CustomCatPricePod
					viewModel={product.PricePodViewModel}
					stockStatus={product.State}
				/>

				<ExVat />
			</div>
			<div className="z-10 mt-auto">
				<div className="flex z-10 py-3">
					<MoreInfo {...product} />
					{product.State !== 'OutOfStock' && <AddToBasketButton {...product} />}
				</div>
				{product.State === 'OutOfStock' && isBelowMediumOnly && (
					<BackInStockForm
						productCode={product.Code}
						productUrl={product.ProductUrl}
						productColourId={product.ColourId}
						productState={product.State}
					/>
				)}
				<div className="mx-auto w-6/12">
					{isMediumAndAbove && (
						<CompareButton
							classes="mt-auto border-none"
							sku={product.Code}
							brand={product.Brand}
						/>
					)}
				</div>

				<CustomCatMobileDeliveryBlock
					deliveryMessage={product.DynamicDeliveryBulletsViewModel[0]}
					stockStatus={product.State}
				/>
			</div>
		</>
	);
};

export default CustomCategoryMobileProductCard;
